import React, { useState, useEffect, useRef, useCallback } from "react";
import SelectBox from "./v2/form/select-box";
import { useTranslation } from "next-i18next";

const tabNameToHashId = (name: string) => name.toLowerCase().replace(/\s+/g, "-");

const PricingProducts = () => {
    const { t } = useTranslation('common');
    const tabsData = t('pricing.tabs', { returnObjects: true, defaultValue: {} }) as any;
    const tabs = Array.isArray(tabsData?.tabs) ? tabsData.tabs : [];
    const content = Array.isArray(tabsData?.content) ? tabsData.content : [];

    const [activeTab, setActiveTab] = useState(tabs.length > 0 ? tabs[0] : "Logs");
    const syncedHashFromUrl = useRef(false);

    const setHashForTab = useCallback((tabName: string) => {
        if (typeof window === "undefined") return;
        const id = tabNameToHashId(tabName);
        const url = `${window.location.pathname}${window.location.search}#${id}`;
        window.history.replaceState(null, "", url);
    }, []);

    const selectTab = useCallback(
        (tabName: string) => {
            setActiveTab(tabName);
            setHashForTab(tabName);
        },
        [setHashForTab]
    );

    useEffect(() => {
        if (tabs.length > 0 && !tabs.includes(activeTab)) {
            setActiveTab(tabs[0]);
        }
    }, [tabs, activeTab]);

    useEffect(() => {
        if (typeof window === "undefined" || tabs.length === 0 || syncedHashFromUrl.current) return;
        const raw = window.location.hash.replace(/^#/, "");
        const hash = decodeURIComponent(raw);
        if (!hash) {
            syncedHashFromUrl.current = true;
            return;
        }
        const match = tabs.find((tab) => tabNameToHashId(tab) === hash);
        if (match) {
            setActiveTab(match);
        }
        syncedHashFromUrl.current = true;
    }, [tabs]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const onHashChange = () => {
            if (tabs.length === 0) return;
            const raw = window.location.hash.replace(/^#/, "");
            const hash = decodeURIComponent(raw);
            if (!hash) return;
            const match = tabs.find((tab) => tabNameToHashId(tab) === hash);
            if (match) {
                setActiveTab(match);
            }
        };
        window.addEventListener("hashchange", onHashChange);
        return () => window.removeEventListener("hashchange", onHashChange);
    }, [tabs]);

    useEffect(() => {
        if (typeof window === "undefined") return;
        const raw = window.location.hash.replace(/^#/, "");
        const hash = decodeURIComponent(raw);
        if (!hash || tabNameToHashId(activeTab) !== hash) return;
        const id = window.setTimeout(() => {
            document.getElementById(hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 0);
        return () => window.clearTimeout(id);
    }, [activeTab]);

    const TabContentHeader = ({ tabContent }) => {
        const words = tabContent.split(' ');
        const firstWord = words.shift();
        const restOfName = words.join(' ');

        return (
            <h3>
                <span className="text-gradient">{firstWord}</span>
                {restOfName && ` ${restOfName}`}
            </h3>
        );
    };

    const renderContent = () => {
        const tabContent = content.find(tab => tab.name === activeTab);
        if (!tabContent) return null;

        return (
            <div className="content" id={tabNameToHashId(tabContent.name)}>
                <TabContentHeader tabContent={tabContent.name} />
                {tabContent.priceData && (
                    <div className={"price-details"}>
                        {tabContent.isOpsAI ? (
                            <div className="opsai-pricing-card">
                                <div className="opsai-pricing-header d-flex">
                                    <p>Error Solving</p>
                                    <p></p>
                                </div>
                                <p className="opsai-pricing-subtext">
                                Additional Token Pricing · Pay per use
                                </p>
                                <p className="opsai-pricing-note">
                                OpsAI automatically uses the best models for deep reasoning and root cause analysis. You only pay for the tokens you use.
                                </p>
                                <div className="opsai-pricing-table-wrap">
                                    <table className="opsai-pricing-table">
                                        <thead>
                                            <tr>
                                                <th>Token Type</th>
                                                <th>Pricing</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td>Input</td>
                                                <td>$3/M token</td>
                                            </tr>
                                            <tr>
                                                <td>Output</td>
                                                <td>$15/M token</td>
                                            </tr>
                                            <tr>
                                                <td>Cache</td>
                                                <td>$6/M token</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                
                            </div>
                        ) : (
                            tabContent.priceData.map((data, index) => (
                                <div className={"d-flex"} key={index}>
                                    <p>{data.Key}</p>
                                    <p>{data.value}</p>
                                </div>
                            ))
                        )}
                    </div>
                )}
                <ul className="features-list">
                    {tabContent.features.map((feature, index) => (
                        <li key={index}>{feature}</li>
                    ))}
                </ul>
            </div>
        );
    };

    return (
        <>
            <section className="pricing-tabs">
                <div className="container">
                    <div className="tab-content">
                        <div className={"d-flex"}>
                            <div className="top-tabs">
                                <ul>
                                    {tabs.map((tab) => (
                                        <li
                                            key={tab}
                                            className={activeTab === tab ? "active" : ""}
                                            onClick={() => selectTab(tab)}
                                        >
                                            {tab}
                                        </li>
                                    ))}
                                </ul>
                                <SelectBox
                                    items={tabs.map((tab) => ({ label: tab, value: tab }))}
                                    defaultSelectedItemIndex={tabs.indexOf(activeTab) >= 0 ? tabs.indexOf(activeTab) : 0}
                                    onChange={(e) => selectTab(e.value)}
                                />
                            </div>
                            <div className={"right-content"}>
                                {renderContent()}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default PricingProducts;
